import { useMemo, useState } from 'react';

import { useEndpoint } from 'api';
import { IntrinsicSizeImg } from 'components/Img';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import Message from 'components/Message';
import Popover from 'components/Popover';
import { PriceText } from 'components/Price';
import {
	FormProvider,
	SubmitButton,
	TextInput,
	useForm,
} from 'components/ReactHookForm';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import Text from 'components/Text';
import { useSelectedStore, useUserContext } from 'contexts';
import { isError } from 'errors';
import { useSessionStorage } from 'hooks';
import type { VariantId } from 'models/product';
import { is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface PostalCodeForm {
	postalCode: string;
}

interface Props {
	description: string;
	isOpen: boolean;
	noResultsText: string;
	onClose: () => void;
	submitButtonText: string;
	title: string;
	variantId: VariantId;
}

export default function DeliveryMethodPopover({
	description,
	isOpen,
	noResultsText,
	onClose,
	submitButtonText,
	title,
	variantId,
}: Props) {
	const { t } = useI18n();
	const { postalAddress } = useUserContext();
	const { selectedStore } = useSelectedStore();

	const { getSessionValue, setSessionValue } = useSessionStorage('POSTAL_CODE');
	const [postalCode, setPostalCode] = useState(
		postalAddress?.postalCode ?? getSessionValue()?.postalCode,
	);
	const handleSubmit = (data: PostalCodeForm) => {
		setPostalCode(data.postalCode);
		setSessionValue({ postalCode: data.postalCode });
	};
	const {
		data: deliveryMethods,
		error,
		isLoading,
	} = useEndpoint(
		isOpen && postalCode
			? {
					name: 'GetDeliveryMethods',
					pathParams: { variantId },
					queryParams: {
						postalCode: postalCode.trim(),
						storeId: selectedStore?.id,
					},
				}
			: null,
	);

	const postalCodeValidationError = useMemo(() => {
		if (isError(error, 'ValidationError')) {
			return {
				postalCode: {
					type: 'fieldError',
					message: error.fieldErrors.postalCode?.[0]?.text ?? undefined,
				},
			};
		}
		return undefined;
	}, [error]);

	const form = useForm<PostalCodeForm>({
		errors: postalCodeValidationError,
	});
	const hasGeneralError = error && !postalCodeValidationError;
	const hasDeliveryMethods = is.arrayWithLength(deliveryMethods);
	const isLoadedOk = !isLoading && !error;
	const noResults = Boolean(isLoadedOk && postalCode && !hasDeliveryMethods);

	return (
		<Popover title={title} isOpen={isOpen} onClose={onClose}>
			<FormProvider form={form} formLocation="DeliveryMethodPopover">
				<form onSubmit={form.handleSubmit(handleSubmit)}>
					{description && (
						<Text as="p" className="mb-4">
							{description}
						</Text>
					)}
					<TextInput
						id="delivery-method-postal-code"
						name="postalCode"
						label={t('general_postal_code_label')}
						className="mb-4"
						defaultValue={postalCode ?? ''}
						required
					/>
					<SubmitButton>{submitButtonText}</SubmitButton>
				</form>
			</FormProvider>
			<div className="mt-8">
				<ScreenReaderAnnouncementText
					as="div"
					atomic
					text={
						<>
							{hasGeneralError && (
								<Message
									variant="error"
									text={t('general_critical_error_text')}
								/>
							)}
							{noResults && (
								<Message variant="information" text={noResultsText} />
							)}
							{isLoadedOk && hasDeliveryMethods && (
								<p className="sr-only">
									{t('general_amount_of_delivery_options', {
										amount: deliveryMethods.length,
									})}
								</p>
							)}
						</>
					}
				/>
				{isLoading && (
					<LoadingSkeleton className="space-y-8">
						{range(5).map((i) => (
							<div key={i} className="flex items-start gap-4">
								<LoadingSkeletonItem height="5rem" width="100%" />
							</div>
						))}
					</LoadingSkeleton>
				)}
				{isLoadedOk && hasDeliveryMethods && (
					<ul className="mt-8 space-y-8">
						{deliveryMethods.map((method) => {
							const logos = method.primaryAssets?.filter(is.truthy);
							return (
								<li key={method.id}>
									<div className="flex justify-between gap-x-4 font-bold">
										<p>{method.name}</p>
										{method.price && (
											<PriceText price={method.price} className="font-bold" />
										)}
									</div>
									<Text as="pSmall">{method.description}</Text>
									{is.arrayWithLength(logos) && (
										<div className="mt-2 flex shrink-0 items-center gap-2">
											{logos.map((logo) => (
												<IntrinsicSizeImg
													key={logo.src}
													asset={logo}
													className="max-h-6 min-h-4 w-auto max-w-20"
												/>
											))}
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</Popover>
	);
}
DeliveryMethodPopover.displayName = 'DeliveryMethodPopover';
